/**
 * API Hooks for Blog Posts
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@/lib/api-client';
import { useAuth, useAuthTokenReady } from './use-auth';

export interface AdminBlogPost {
  id: string;
  title: string;
  slug: string;
  description?: string;
  excerpt?: string;
  content?: string;
  type: 'article' | 'video';
  status: 'draft' | 'published' | 'archived';
  thumbnail_url?: string;
  youtube_url?: string;
  read_time?: string;
  duration?: string;
  tags?: string[];
  category?: string;
  author_id: string;
  expert_id?: string;
  author_name?: string;
  author_email?: string;
  author_avatar?: string;
  views: number;
  likes: number;
  seo_title?: string;
  seo_description?: string;
  published_at?: string;
  created_at: string;
  updated_at: string;
}

export interface BlogListParams {
  page?: number;
  pageSize?: number;
  query?: string;
  status?: 'all' | 'draft' | 'published' | 'archived';
  category?: string;
  type?: 'all' | 'article' | 'video';
  sortBy?: 'created_at' | 'updated_at' | 'published_at' | 'views' | 'title';
  sortDir?: 'asc' | 'desc';
}

export interface BlogListResponse {
  posts: AdminBlogPost[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface BlogStatusCounts {
  all: number;
  draft: number;
  published: number;
  archived: number;
}

export interface CreateBlogPostPayload {
  title: string;
  slug?: string;
  description?: string;
  excerpt?: string;
  content?: string;
  type?: 'article' | 'video';
  status?: 'draft' | 'published';
  thumbnail_url?: string;
  youtube_url?: string;
  read_time?: string;
  duration?: string;
  tags?: string[];
  category?: string;
  author_id: string;
  expert_id?: string;
  seo_title?: string;
  seo_description?: string;
}

export interface UpdateBlogPostPayload {
  title?: string;
  slug?: string;
  description?: string;
  excerpt?: string;
  content?: string;
  type?: 'article' | 'video';
  status?: 'draft' | 'published' | 'archived';
  thumbnail_url?: string;
  youtube_url?: string;
  read_time?: string;
  duration?: string;
  tags?: string[];
  category?: string;
  expert_id?: string;
  seo_title?: string;
  seo_description?: string;
}

export const blogKeys = {
  all: ['blog'] as const,
  lists: () => [...blogKeys.all, 'list'] as const,
  list: (params: BlogListParams) => [...blogKeys.lists(), params] as const,
  details: () => [...blogKeys.all, 'detail'] as const,
  detail: (id: string) => [...blogKeys.details(), id] as const,
  statusCounts: () => [...blogKeys.all, 'statusCounts'] as const,
  tags: () => [...blogKeys.all, 'tags'] as const,
};

/**
 * Hook to fetch blog posts list with pagination and filters
 */
export function useBlogPosts(params: BlogListParams = {}) {
  const apiClient = getApiClient();
  const { user, loading } = useAuth();
  const tokenReady = useAuthTokenReady();

  const defaultParams = {
    page: 1,
    pageSize: 10,
    sortBy: 'updated_at',
    sortDir: 'desc' as const,
    ...params
  };

  // Debug logging for enabled condition
  console.log('useBlogPosts: Auth state', {
    loading,
    hasUser: !!user,
    userEmail: user?.email,
    tokenReady,
    enabled: !loading && !!user && tokenReady,
  });

  return useQuery<BlogListResponse>({
    queryKey: blogKeys.list(defaultParams),
    queryFn: async () => {
      try {
        const searchParams = new URLSearchParams();
        Object.entries(defaultParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value));
          }
        });

        console.log('Fetching blog posts with params:', searchParams.toString());

        const response = await apiClient.request<BlogListResponse>(
          `/blog/posts?${searchParams.toString()}`
        );
        console.log('Blog posts response:', response);
        return response;
      } catch (error) {
        console.error('Error fetching blog posts from API:', error);
        console.error('Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });

        // Re-throw error to let React Query handle it properly
        // This will show error state in UI instead of mock data
        throw error;
      }
    },
    enabled: !loading && !!user && tokenReady,
  });
}

/**
 * Hook to fetch a single blog post
 */
export function useBlogPost(id: string) {
  const apiClient = getApiClient();
  const { user, loading } = useAuth();
  const tokenReady = useAuthTokenReady();

  return useQuery<AdminBlogPost>({
    queryKey: blogKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.request<{ post: AdminBlogPost }>(`/blog/posts/${id}`);
      console.log('useBlogPost response:', {
        id: response.post?.id,
        title: response.post?.title,
        hasContent: !!response.post?.content,
        contentLength: response.post?.content?.length || 0,
        contentValue: response.post?.content,
        hasDescription: !!response.post?.description,
        descriptionValue: response.post?.description,
        hasExcerpt: !!response.post?.excerpt,
        excerptValue: response.post?.excerpt,
        tags: response.post?.tags,
        category: response.post?.category,
        fullResponse: response,
        fullPost: response.post,
      });
      return response.post;
    },
    enabled: !loading && !!user && tokenReady && !!id,
  });
}

/**
 * Hook to fetch blog status counts
 */
export function useBlogStatusCounts() {
  const apiClient = getApiClient();
  const { user, loading } = useAuth();
  const tokenReady = useAuthTokenReady();

  return useQuery<BlogStatusCounts>({
    queryKey: blogKeys.statusCounts(),
    queryFn: async () => {
      try {
        console.log('Fetching blog status counts');
        const response = await apiClient.request<{ counts: BlogStatusCounts }>('/blog/posts/counts');
        console.log('Blog status counts response:', response);
        return response.counts;
      } catch (error) {
        console.error('Error fetching blog status counts from API:', error);
        console.error('Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
        
        // Re-throw error to let React Query handle it properly
        throw error;
        
        // Return mock data as fallback (commented out to see real errors)
        // return {
        //   all: 3,
        //   published: 2,
        //   draft: 1,
        //   archived: 0,
        // };
      }
    },
    enabled: !loading && !!user && tokenReady,
  });
}

/**
 * Hook to create a new blog post
 */
export function useCreateBlogPost() {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();

  return useMutation<AdminBlogPost, Error, CreateBlogPostPayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.request<{ post: AdminBlogPost }>(
        '/blog/posts',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      );
      return response.post;
    },
    onSuccess: () => {
      // Invalidate blog lists to refresh counts and list
      queryClient.invalidateQueries({ queryKey: blogKeys.lists() });
      queryClient.invalidateQueries({ queryKey: blogKeys.statusCounts() });
    },
  });
}

/**
 * Hook to update a blog post
 */
export function useUpdateBlogPost() {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();

  return useMutation<AdminBlogPost, Error, { id: string; payload: UpdateBlogPostPayload }>({
    mutationFn: async ({ id, payload }) => {
      const response = await apiClient.request<{ post: AdminBlogPost }>(
        `/blog/posts/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        }
      );
      return response.post;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch blog detail
      queryClient.invalidateQueries({ queryKey: blogKeys.detail(variables.id) });
      // Invalidate blog lists to refresh counts
      queryClient.invalidateQueries({ queryKey: blogKeys.lists() });
      queryClient.invalidateQueries({ queryKey: blogKeys.statusCounts() });
    },
  });
}

/**
 * Hook to delete a blog post
 */
export function useDeleteBlogPost() {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await apiClient.request(`/blog/posts/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      // Invalidate blog lists to refresh counts and list
      queryClient.invalidateQueries({ queryKey: blogKeys.lists() });
      queryClient.invalidateQueries({ queryKey: blogKeys.statusCounts() });
    },
  });
}

/**
 * Legacy hook for backward compatibility
 */
export function useBlogPostsLegacy() {
  const apiClient = getApiClient();
  const { user, loading } = useAuth();
  const tokenReady = useAuthTokenReady();

  return useQuery<AdminBlogPost[]>({
    queryKey: blogKeys.list({ page: 1, pageSize: 50 }),
    queryFn: async () => {
      const response = await apiClient.request<{ posts: AdminBlogPost[] }>('/admin/blog');
      return response.posts;
    },
    enabled: !loading && !!user && tokenReady,
  });
}

export function useBlogPostLegacy(id: string) {
  const apiClient = getApiClient();
  const { user, loading } = useAuth();
  const tokenReady = useAuthTokenReady();

  return useQuery<AdminBlogPost>({
    queryKey: blogKeys.detail(id),
    queryFn: async () => {
      return apiClient.request<AdminBlogPost>(`/blog/${id}`);
    },
    enabled: !loading && !!user && tokenReady && !!id,
  });
}

/**
 * Hook to fetch all unique tags from blog posts
 */
export function useBlogTags() {
  const apiClient = getApiClient();
  const { user, loading } = useAuth();
  const tokenReady = useAuthTokenReady();

  return useQuery<string[]>({
    queryKey: blogKeys.tags(),
    queryFn: async () => {
      // Fetch a large number of posts to get all tags
      const response = await apiClient.request<BlogListResponse>(
        `/blog/posts?page=1&pageSize=200&status=all`
      );
      
      // Extract unique tags from all posts
      const tagSet = new Set<string>();
      response.posts.forEach((post) => {
        if (post.tags && Array.isArray(post.tags)) {
          post.tags.forEach((tag) => {
            if (tag && typeof tag === 'string' && tag.trim()) {
              tagSet.add(tag.trim());
            }
          });
        }
      });
      
      // Return sorted array of unique tags
      return Array.from(tagSet).sort();
    },
    enabled: !loading && !!user && tokenReady,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

